/**
 * <prelaunch-video> — background video controller for the pre-launch page.
 *
 * Two jobs:
 *
 * 1. Variant pick: with a desktop AND a mobile crop configured, the <video
 *    data-variant> children ship with preload="none" and no autoplay so
 *    neither downloads by itself; the one matching the viewport is kept and
 *    the other removed (hiding via CSS would not stop the download). Chosen
 *    once per page load. With a single video the markup autoplays natively.
 *
 * 2. Autoplay resilience: iOS refuses autoplay in Low Power Mode / Low Data
 *    Mode / with "Auto-Play Video Previews" off, and play() rejects. That
 *    policy can't be overridden, so retry at the moments it can succeed —
 *    once media data arrives, when the tab becomes visible again, and on the
 *    visitor's first touch/click anywhere (a gesture satisfies the policy).
 *    Until then the poster image shows.
 */
class PrelaunchVideo extends HTMLElement {
  connectedCallback() {
    const variants = Array.from(this.querySelectorAll('video[data-variant]'));

    if (variants.length > 1) {
      const keep = window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
      variants.forEach((video) => {
        if (video.dataset.variant !== keep) video.remove();
      });
    }

    this.video = this.querySelector('video');
    if (!this.video) return;

    // Set properties as well as attributes — some iOS versions only honour
    // the property form when playback is started from script.
    this.video.muted = true;
    this.video.playsInline = true;

    if (this.video.preload === 'none') {
      this.video.preload = 'auto';
      this.video.autoplay = true;
      this.video.load();
    }

    this.tryPlay = this.tryPlay.bind(this);

    this.video.addEventListener('loadeddata', this.tryPlay);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.tryPlay();
    });
    ['touchend', 'click'].forEach((type) =>
      document.addEventListener(type, this.tryPlay, { once: true, passive: true })
    );

    this.tryPlay();
  }

  tryPlay() {
    if (!this.video || !this.video.paused || !this.video.isConnected) return;
    const playing = this.video.play();
    if (playing) playing.catch(() => {});
  }
}

if (!customElements.get('prelaunch-video')) {
  customElements.define('prelaunch-video', PrelaunchVideo);
}
